import Button from './Button';
import Modal from './Modal';

type Tone = 'danger' | 'primary' | 'warning';

const TONE_STYLES: Record<Tone, { wrap: string; defaultIcon: string }> = {
  danger: { wrap: 'bg-rose-50 text-rose-600', defaultIcon: 'ri-delete-bin-6-line' },
  primary: { wrap: 'bg-primary-100 text-primary-700', defaultIcon: 'ri-question-line' },
  warning: { wrap: 'bg-amber-50 text-amber-600', defaultIcon: 'ri-eye-off-line' },
};

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  text: string;
  confirmLabel: string;
  cancelLabel: string;
  tone?: Tone;
  icon?: string;
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export default function ConfirmDialog({
  open,
  title,
  text,
  confirmLabel,
  cancelLabel,
  tone = 'danger',
  icon,
  loading = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const styles = TONE_STYLES[tone];

  return (
    <Modal
      open={open}
      onClose={loading ? () => undefined : onClose}
      size="sm"
      footer={
        <>
          <Button variant="outline" size="md" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            size="md"
            loading={loading}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex items-start gap-3">
        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${styles.wrap}`}>
          <i className={`${icon ?? styles.defaultIcon} text-xl leading-none`} aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-foreground-950">{title}</h3>
          <p className="mt-1 text-sm leading-relaxed text-foreground-600">{text}</p>
        </div>
      </div>
    </Modal>
  );
}